import { ALL_ACTIONS, ALL_ENTITIES } from '@/pages/roles/roleEntities';
import type { User } from '@/types';

/**
 * Utility functions for handling user permissions
 */

type PermissionRule = {
  entity: string;
  action: string;
};

/**
 * Safely extract role permissions from a user.
 */
const getUserPermissions = (
  user: User | null
): Record<string, string[]> | null => {
  if (!user || !user.role) {
    return null;
  }

  return user.role.permissions;
};

/**
 * Normalize entity permissions to a guaranteed string array.
 */
const getEntityPermissions = (
  permissions: Record<string, string[]> | null,
  entity: string
): string[] => {
  if (!permissions) {
    return [];
  }

  const entityPermissions = permissions[entity];
  return Array.isArray(entityPermissions) ? entityPermissions : [];
};

/**
 * Check if a user has permission to perform a specific action on an entity
 * @param user - The authenticated user
 * @param entity - The entity name (e.g., 'users', 'roles', 'logs')
 * @param action - The action to check (e.g., 'read', 'create', 'update', 'delete')
 * @returns boolean indicating if the user has permission
 */
export const hasPermission = (
  user: User | null,
  entity: string,
  action: string
): boolean => {
  // Superadmin has access to everything
  if (isSuperAdmin(user)) {
    return true;
  }

  const entityPermissions = getEntityPermissions(
    getUserPermissions(user),
    entity
  );
  return entityPermissions.includes(action);
};

/**
 * Check if a user has any permission for a specific entity
 * @param user - The authenticated user
 * @param entity - The entity name (e.g., 'users', 'roles', 'logs')
 * @returns boolean indicating if the user has any permission for the entity
 */
export const hasAnyPermission = (
  user: User | null,
  entity: string
): boolean => {
  // Superadmin has access to everything
  if (isSuperAdmin(user)) {
    return true;
  }

  const entityPermissions = getEntityPermissions(
    getUserPermissions(user),
    entity
  );
  return entityPermissions.length > 0;
};

/**
 * Check if a user is a superadmin
 * @param user - The authenticated user
 * @returns boolean indicating if the user is a superadmin
 */
export const isSuperAdmin = (user: User | null): boolean => {
  return Boolean(user?.role?.superadmin);
};

/**
 * Check if a user has multiple permissions
 * @param user - The authenticated user
 * @param permissions - Array of permission objects with entity and action
 * @returns boolean indicating if the user has all specified permissions
 */
export const hasAllPermissions = (
  user: User | null,
  permissions: PermissionRule[]
): boolean => {
  if (isSuperAdmin(user)) {
    return true;
  }

  return permissions.every(({ entity, action }) =>
    hasPermission(user, entity, action)
  );
};

/**
 * Check if a user has any of the specified permissions
 * @param user - The authenticated user
 * @param permissions - Array of permission objects with entity and action
 * @returns boolean indicating if the user has any of the specified permissions
 */
export const hasAnyOfPermissions = (
  user: User | null,
  permissions: PermissionRule[]
): boolean => {
  if (isSuperAdmin(user)) {
    return true;
  }

  return permissions.some(({ entity, action }) =>
    hasPermission(user, entity, action)
  );
};

/**
 * Get all entities that a user has access to
 * @param user - The authenticated user
 * @returns Array of entity names that the user has access to
 */
export const getAccessibleEntities = (user: User | null): string[] => {
  // Superadmin has access to everything - return common entities
  if (isSuperAdmin(user)) {
    return ALL_ENTITIES; // Add more entities as needed
  }

  const permissions = getUserPermissions(user);
  if (!permissions) {
    return [];
  }

  // Return entities that have any permissions
  return Object.keys(permissions).filter(
    entity => getEntityPermissions(permissions, entity).length > 0
  );
};

/**
 * Get all actions a user can perform on a specific entity
 * @param user - The authenticated user
 * @param entity - The entity name
 * @returns Array of action names the user can perform
 */
export const getEntityActions = (
  user: User | null,
  entity: string
): string[] => {
  // Superadmin has access to everything
  if (isSuperAdmin(user)) {
    return ALL_ACTIONS; // Common CRUD actions
  }

  return getEntityPermissions(getUserPermissions(user), entity);
};
